import React, { PureComponent } from 'react';
import PropTypes from 'prop-types';
import { connect, Provider } from 'react-redux';
import { createStore } from 'redux-magic-reducer';
export { createStore } from 'redux-magic-reducer';

/**
 * Copyright (c) 2013-present, Facebook, Inc.
 *
 * This source code is licensed under the MIT license found in the
 * LICENSE file in the root directory of this source tree.
 */

/**
 * Use invariant() to assert state which your program assumes to be true.
 *
 * Provide sprintf-style format (only %s is supported) and arguments
 * to provide information about what broke and what you were
 * expecting.
 *
 * The invariant message will be stripped in production, but the invariant
 * will remain to ensure logic does not differ in production.
 */

var NODE_ENV = process.env.NODE_ENV;

var invariant = function(condition, format, a, b, c, d, e, f) {
  if (NODE_ENV !== 'production') {
    if (format === undefined) {
      throw new Error('invariant requires an error message argument');
    }
  }

  if (!condition) {
    var error;
    if (format === undefined) {
      error = new Error(
        'Minified exception occurred; use the non-minified dev environment ' +
        'for the full error message and additional helpful warnings.'
      );
    } else {
      var args = [a, b, c, d, e, f];
      var argIndex = 0;
      error = new Error(
        format.replace(/%s/g, function() { return args[argIndex++]; })
      );
      error.name = 'Invariant Violation';
    }

    error.framesToPop = 1; // we don't care about invariant's own frame
    throw error;
  }
};

var invariant_1 = invariant;

var classCallCheck = function (instance, Constructor) {
  if (!(instance instanceof Constructor)) {
    throw new TypeError("Cannot call a class as a function");
  }
};

var createClass = function () {
  function defineProperties(target, props) {
    for (var i = 0; i < props.length; i++) {
      var descriptor = props[i];
      descriptor.enumerable = descriptor.enumerable || false;
      descriptor.configurable = true;
      if ("value" in descriptor) descriptor.writable = true;
      Object.defineProperty(target, descriptor.key, descriptor);
    }
  }

  return function (Constructor, protoProps, staticProps) {
    if (protoProps) defineProperties(Constructor.prototype, protoProps);
    if (staticProps) defineProperties(Constructor, staticProps);
    return Constructor;
  };
}();

var inherits = function (subClass, superClass) {
  if (typeof superClass !== "function" && superClass !== null) {
    throw new TypeError("Super expression must either be null or a function, not " + typeof superClass);
  }

  subClass.prototype = Object.create(superClass && superClass.prototype, {
    constructor: {
      value: subClass,
      enumerable: false,
      writable: true,
      configurable: true
    }
  });
  if (superClass) Object.setPrototypeOf ? Object.setPrototypeOf(subClass, superClass) : subClass.__proto__ = superClass;
};

var possibleConstructorReturn = function (self, call) {
  if (!self) {
    throw new ReferenceError("this hasn't been initialised - super() hasn't been called");
  }

  return call && (typeof call === "object" || typeof call === "function") ? call : self;
};

var magicConnect = function magicConnect(mapStateToPropsFactories, mapDispatchToPropsFactories, mergePropsFactories, selectorFactory) {
  return function (WrappedComponent, dynamicPath, reducer) {
    var MagicConnect = function (_PureComponent) {
      inherits(MagicConnect, _PureComponent);
      createClass(MagicConnect, [{
        key: 'getChildContext',
        value: function getChildContext() {
          return {
            store: this.context.store
          };
        }
      }]);

      function MagicConnect(props, context) {
        classCallCheck(this, MagicConnect);

        var _this = possibleConstructorReturn(this, (MagicConnect.__proto__ || Object.getPrototypeOf(MagicConnect)).call(this, props, context));

        var storeKey = props.storeKey || 'store';
        _this.store = props[storeKey] || context[storeKey];

        invariant_1(_this.store, 'Could not find "' + storeKey + '" in either the context or props of. Either wrap the root component in a <Provider>, ' + ('or explicitly pass "' + storeKey + '" to "' + _this.getDisplayName() + '" as a prop.'));

        _this.addReducer(_this.store, dynamicPath, reducer);
        return _this;
      }

      createClass(MagicConnect, [{
        key: 'getDisplayName',
        value: function getDisplayName() {
          return 'MagicConnect(' + this.displayName + ')';
        }
      }, {
        key: 'addReducer',
        value: function addReducer(store, path, reducer) {
          invariant_1(store.attachReducer, 'Could not find "attachReducer" method in store. Make sure you are passing store created from redux-magic-reducer');
          store.attachReducer(path, reducer);
        }
      }, {
        key: 'render',
        value: function render() {
          var ConnectedComponent = connect(mapStateToPropsFactories, mapDispatchToPropsFactories, mergePropsFactories, selectorFactory)(WrappedComponent);
          return React.createElement(ConnectedComponent, this.props);
        }
      }]);
      return MagicConnect;
    }(PureComponent);

    MagicConnect.displayName = WrappedComponent.displayName || WrappedComponent.name;
    MagicConnect.contextTypes = {
      store: PropTypes.object
    };
    MagicConnect.childContextTypes = {
      store: PropTypes.object
    };


    return MagicConnect;
  };
};

/**
 * This is a necessary abstraction for root component which I find always bit tricky to get right in one go.
 * To avoid common errors with contextTypes and childContextTypes, it is advised to use this instead of creating provider.
 * 
 * You can find why it is required to pass childContextTypes here : https://egghead.io/lessons/react-redux-passing-the-store-down-implicitly-via-context
 * 
 * Props must contain store.
 * contextTypes, childContextTypes and getChildContext are optional.
 * However, I find it necessary to give a override mechanism for them as they might be required in cases like using router.
 * 
 * @param {*} BaseComponent 
 * @param {*} props 
 * @param {*} contextTypes 
 * @param {*} childContextTypes 
 * @param {*} getChildContext 
 */
var withProvider = function withProvider(BaseComponent, props, contextTypes, childContextTypes, getChildContext) {
    /**
     * If passed contextTypes it will ignore default mentioned below.
     * Make sure to have store in passed one if you are passing contextTypes
     */
    var targetContextTypes = contextTypes ? contextTypes : {
        store: PropTypes.object
    };

    /**
     * If passed childContextTypes it will ignore default mentioned below.
     * Make sure to have store in passed one if you are passing childContextTypes
     */
    var targetChildContextTypes = childContextTypes ? childContextTypes : {
        store: PropTypes.object

        /**
         * If passed getChildContext it will ignore default function mentioned below.
         * Make sure to have store in passed one if you are passing getChildContext
         * please take a look at for why it is needed : https://egghead.io/lessons/react-redux-passing-the-store-down-implicitly-via-context
         */
    };var targetGetChildContext = getChildContext ? getChildContext : function () {
        return {
            store: this.context.store
        };
    };

    var WrappedComponent = function (_PureComponent) {
        inherits(WrappedComponent, _PureComponent);

        function WrappedComponent() {
            var _ref;

            var _temp, _this, _ret;

            classCallCheck(this, WrappedComponent);

            for (var _len = arguments.length, args = Array(_len), _key = 0; _key < _len; _key++) {
                args[_key] = arguments[_key];
            }

            return _ret = (_temp = (_this = possibleConstructorReturn(this, (_ref = WrappedComponent.__proto__ || Object.getPrototypeOf(WrappedComponent)).call.apply(_ref, [this].concat(args))), _this), _this.getChildContext = targetGetChildContext, _temp), possibleConstructorReturn(_this, _ret);
        }

        createClass(WrappedComponent, [{
            key: 'render',
            value: function render() {
                return React.createElement(BaseComponent, this.props);
            }
        }]);
        return WrappedComponent;
    }(PureComponent);

    WrappedComponent.contextTypes = targetContextTypes;
    WrappedComponent.childContextTypes = targetChildContextTypes;


    return React.createElement(
        Provider,
        props,
        React.createElement(WrappedComponent, props)
    );
};

export { magicConnect, withProvider };
//# sourceMappingURL=index.es.js.map



//////////////////
// WEBPACK FOOTER
// ../dist/index.es.js
// module id = 7
// module chunks = 0